import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PaymentsApi } from '../../services/api/PaymentsApi';
import { PaymentsServiceApi } from '../../services/PaymentsServiceApi';
import { 
  MetricCard, 
  LoadingChart, 
  EmptyState, 
  DateRangePicker, 
  ExportButton, 
  InsightCard,
} from '../../components/analytics/SharedAnalyticsComponents';
import { 
  Bar,
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ComposedChart,
  FunnelChart,
  Funnel,
  LabelList
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14']; // eslint-disable-line no-unused-vars

/**
 * PaymentsAdminDashboard - Enhanced Payment Analytics
 * MRR/ARR tracking, churn analysis, payment funnel, refund trends, admin tools (tabbed)
 */
export default function PaymentsAdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics' | 'reconciliation' | 'admin-tools'
  
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 30 * 86400000); // 30 days
    return { from: startDate.toISOString(), to: endDate.toISOString() };
  });

  const [data, setData] = useState({
    payments: [],
    summary: null,
    serviceSummary: null,
    mrr: [],
    churn: [],
    funnel: [],
    refundTrends: [],
    plans: [],
    monthlyRevenue: [],
    reconciliation: {
      stripePayments: [],
      stripeTotals: { count: 0, amount: 0 },
      webhookSummary: null,
      webhookEventTypes: []
    }
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Admin tools state
  const [lookupUserId, setLookupUserId] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  const fetchPaymentsList = useCallback(async () => {
    const maxPages = 10;
    const pageSize = 100;
    const payments = [];
    let cursor = null;

    const fromDate = dateRange.from ? new Date(dateRange.from) : null;

    for (let page = 0; page < maxPages; page += 1) {
      try {
        const res = await PaymentsApi.list({
          limit: pageSize,
          status: '',
          provider: '',
          cursor,
          from: dateRange.from,
          to: dateRange.to
        });
        if (!res?.ok) {
          console.warn('[PaymentsDashboard] list returned non-ok status:', res?.status, res?.statusText);
          break;
        }

        const json = await res.json();
        const items = Array.isArray(json?.items) ? json.items : (Array.isArray(json) ? json : []);
        if (items.length === 0) break;

        payments.push(...items);

        cursor = json?.nextCursor || null;
        if (!cursor) break;

        if (fromDate) {
          const oldest = items[items.length - 1]?.createdAt;
          if (oldest && new Date(oldest) < fromDate) {
            break;
          }
        }
      } catch (err) {
        console.error('[PaymentsDashboard] Error fetching payments page', page, err);
        break;
      }
    }

    return payments;
  }, [dateRange]);

  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);

    try {
      const [
        paymentsList,
        summaryRes,
        monthlyRevenueRes,
        serviceSummaryRes,
        plansRes,
        stripePayments,
        webhookSummary,
        webhookEventTypes
      ] = await Promise.all([
        fetchPaymentsList().catch((err) => { console.warn('[PaymentsDashboard] fetchPaymentsList error:', err); return []; }),
        PaymentsApi.summary({ from: dateRange.from, to: dateRange.to })
          .then(r => r.ok ? r.json() : null).catch(() => null),
        PaymentsApi.monthlyRevenue({ from: dateRange.from, to: dateRange.to })
          .then(r => r.ok ? r.json() : null).catch(() => null),
        PaymentsServiceApi.getPaymentsSummary()
          .then(res => res).catch(() => null),
        PaymentsServiceApi.listPlans()
          .then(plans => plans).catch(() => []),
        PaymentsServiceApi.listPayments({
          fromDate: dateRange.from,
          toDate: dateRange.to,
          limit: 1000
        }).catch(() => []),
        PaymentsApi.getPaymentEventSummary({ from: dateRange.from, to: dateRange.to })
          .then(r => r.ok ? r.json() : null).catch(() => null),
        PaymentsApi.getPaymentEventTypeDistribution({ from: dateRange.from, to: dateRange.to })
          .then(r => r.ok ? r.json() : []).catch(() => [])
      ]);

      // Process payments data
      const payments = Array.isArray(paymentsList) ? paymentsList : [];
      
      // Use server-side monthly revenue if available, otherwise fall back to client calculation
      const serverMonths = Array.isArray(monthlyRevenueRes?.months) ? monthlyRevenueRes.months : [];
      const mrrData = serverMonths.length > 0
        ? serverMonths.map(m => ({
            month: m.month,
            mrr: Number((m.mrr || 0).toFixed(2)),
            arr: Number((m.arr || 0).toFixed(2)),
            newCustomers: 0,
            churnedCustomers: 0
          }))
        : calculateMRR(payments);
      
      // Calculate churn from payments (needs per-user data)
      const churnData = calculateChurn(payments);
      
      // Payment funnel
      const funnelData = calculatePaymentFunnel(payments);
      
      // Refund trends
      const refundTrends = calculateRefundTrends(payments);

      const stripeList = Array.isArray(stripePayments) ? stripePayments : (stripePayments?.items || stripePayments?.data || []);
      const stripeTotals = stripeList.reduce((acc, p) => {
        const amount = Number(p.amount || p.amount_total || p.totalAmount || 0);
        acc.count += 1;
        acc.amount += amount;
        return acc;
      }, { count: 0, amount: 0 });

      setData({
        payments,
        summary: summaryRes,
        serviceSummary: serviceSummaryRes,
        mrr: mrrData,
        churn: churnData,
        funnel: funnelData,
        refundTrends,
        plans: plansRes,
        monthlyRevenue: serverMonths,
        reconciliation: {
          stripePayments: stripeList,
          stripeTotals,
          webhookSummary,
          webhookEventTypes
        }
      });
    } catch (err) {
      setError(err);
      console.error('Failed to load payments data:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, isAdmin, fetchPaymentsList]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const calculateMRR = (payments) => {
    // Group recurring (succeeded/completed) payments by month to derive MRR
    const monthMap = {};

    payments.forEach(p => {
      if (p.status !== 'succeeded' && p.status !== 'completed') return;
      const date = new Date(p.createdAt || p.created_at);
      if (isNaN(date)) return;
      const monthKey = date.toISOString().slice(0, 7);

      if (!monthMap[monthKey]) {
        monthMap[monthKey] = { revenue: 0, users: new Set() };
      }
      monthMap[monthKey].revenue += (p.amount || 0) / 100; // cents → dollars
      if (p.userId || p.user_id) monthMap[monthKey].users.add(p.userId || p.user_id);
    });

    const sortedMonths = Object.keys(monthMap).sort();
    let prevUsers = new Set();

    return sortedMonths.map(month => {
      const { revenue, users } = monthMap[month];
      const newCustomers = [...users].filter(u => !prevUsers.has(u)).length;
      const churnedCustomers = [...prevUsers].filter(u => !users.has(u)).length;
      prevUsers = new Set(users);

      return {
        month,
        mrr: Number(revenue.toFixed(2)),
        arr: Number((revenue * 12).toFixed(2)),
        newCustomers,
        churnedCustomers
      };
    });
  };

  const calculateChurn = (payments) => {
    // Calculate real churn: compare unique paying users month-over-month
    const usersByMonth = {};

    payments.forEach(p => {
      if (p.status !== 'succeeded' && p.status !== 'completed') return;
      const uid = p.userId || p.user_id;
      if (!uid) return;
      const date = new Date(p.createdAt || p.created_at);
      if (isNaN(date)) return;
      const monthKey = date.toISOString().slice(0, 7);

      if (!usersByMonth[monthKey]) usersByMonth[monthKey] = new Set();
      usersByMonth[monthKey].add(uid);
    });

    const sortedMonths = Object.keys(usersByMonth).sort();
    const churnData = [];

    for (let i = 0; i < sortedMonths.length; i++) {
      const month = sortedMonths[i];
      const currentUsers = usersByMonth[month];
      const prevUsers = i > 0 ? usersByMonth[sortedMonths[i - 1]] : new Set();

      const churned = i > 0 ? [...prevUsers].filter(u => !currentUsers.has(u)).length : 0;
      const totalPrev = prevUsers.size;
      const churnRate = totalPrev > 0 ? ((churned / totalPrev) * 100).toFixed(2) : '0.00';

      churnData.push({
        month,
        churnRate,
        churned,
        retained: currentUsers.size
      });
    }

    return churnData;
  };

  const calculatePaymentFunnel = (payments) => {
    const total = payments.length;
    const attempted = total;
    const processing = payments.filter(p => p.status === 'processing').length;
    const succeeded = payments.filter(p => p.status === 'succeeded' || p.status === 'completed').length;
    const failed = payments.filter(p => p.status === 'failed').length;

    return [
      { name: 'Attempted', value: attempted, fill: '#007bff' },
      { name: 'Processing', value: processing, fill: '#ffc107' },
      { name: 'Succeeded', value: succeeded, fill: '#28a745' },
      { name: 'Failed', value: failed, fill: '#dc3545' }
    ].filter(s => s.value > 0);
  };

  const calculateRefundTrends = (payments) => {
    // Group refunds by day
    const refundsByDay = {};
    
    payments.forEach(payment => {
      if (payment.status === 'refunded') {
        const date = new Date(payment.createdAt || payment.created_at);
        const dateKey = date.toISOString().split('T')[0];
        
        if (!refundsByDay[dateKey]) {
          refundsByDay[dateKey] = { date: dateKey, count: 0, amount: 0 };
        }
        
        refundsByDay[dateKey].count++;
        refundsByDay[dateKey].amount += payment.amount || 0;
      }
    });
    
    return Object.values(refundsByDay).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const lookupSubscription = async () => {
    if (!lookupUserId) return;
    
    setLookupLoading(true);
    setLookupError(null);
    
    try {
      const res = await PaymentsApi.getSubscription(lookupUserId);
      if (res.ok) {
        const sub = await res.json();
        setSubscription(sub);
      } else if (res.status === 404) {
        setSubscription(null);
        setLookupError('No subscription found for this user (may be on free tier)');
      } else {
        throw new Error(`Failed to fetch subscription: ${res.status}`);
      }
    } catch (err) {
      setLookupError(err.message);
      setSubscription(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const generateInsights = () => {
    const insights = [];
    
    // MRR growth
    if (data.mrr.length >= 2) {
      const latestMRR = data.mrr[data.mrr.length - 1].mrr;
      const previousMRR = data.mrr[data.mrr.length - 2].mrr;
      const growth = ((latestMRR - previousMRR) / previousMRR * 100).toFixed(1);
      
      if (growth > 10) {
        insights.push({
          title: '📈 Strong MRR Growth',
          description: `MRR grew ${growth}% month-over-month ($${previousMRR.toFixed(0)} → $${latestMRR.toFixed(0)})`,
          severity: 'success'
        });
      } else if (growth < -5) {
        insights.push({
          title: '📉 MRR Decline',
          description: `MRR decreased ${Math.abs(growth)}% month-over-month. Review churn and acquisition.`,
          severity: 'danger'
        });
      }
    }

    // Churn rate
    if (data.churn.length > 0) {
      const latestChurn = parseFloat(data.churn[data.churn.length - 1].churnRate);
      if (latestChurn > 5) {
        insights.push({
          title: '⚠️ High Churn Rate',
          description: `Current churn rate is ${latestChurn}%. Industry benchmark is typically 3-5%.`,
          severity: 'warning'
        });
      }
    }

    // Failed payments — use summary if available, fall back to client-side
    const s = data.summary || {};
    const totalCount = s.totalCount || data.payments.length;
    const failedCount = data.payments.filter(p => p.status === 'failed').length;
    if (totalCount > 0 && failedCount > totalCount * 0.1) {
      insights.push({
        title: '🚨 High Failure Rate',
        description: `${failedCount} of ${totalCount} payments failed (${(failedCount / totalCount * 100).toFixed(1)}%)`,
        severity: 'danger'
      });
    }

    return insights;
  };

  const insights = generateInsights();

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Payments Dashboard</h2>
        <EmptyState message="Admin access required" icon="🔒" />
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>💰 Payments Dashboard</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangePicker 
            from={dateRange.from} 
            to={dateRange.to} 
            onChange={setDateRange}
          />
          <button onClick={loadData} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid var(--border-light)' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setActiveTab('analytics')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'analytics' ? 'var(--semantic-primary)' : 'transparent',
              color: activeTab === 'analytics' ? 'var(--semantic-text-inverse)' : 'var(--text-primary)',
              border: 'none',
              borderBottom: activeTab === 'analytics' ? '3px solid var(--semantic-primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '.85rem'
            }}
          >
            📊 Analytics
          </button>
          <button
            onClick={() => setActiveTab('reconciliation')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'reconciliation' ? 'var(--semantic-primary)' : 'transparent',
              color: activeTab === 'reconciliation' ? 'var(--semantic-text-inverse)' : 'var(--text-primary)',
              border: 'none',
              borderBottom: activeTab === 'reconciliation' ? '3px solid var(--semantic-primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '.85rem'
            }}
          >
            ✅ Reconciliation
          </button>
          <button
            onClick={() => setActiveTab('admin-tools')}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === 'admin-tools' ? 'var(--semantic-primary)' : 'transparent',
              color: activeTab === 'admin-tools' ? 'var(--semantic-text-inverse)' : 'var(--text-primary)',
              border: 'none',
              borderBottom: activeTab === 'admin-tools' ? '3px solid var(--semantic-primary)' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '.85rem'
            }}
          >
            🔧 Admin Tools
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--semantic-surface-danger)', border: '1px solid var(--semantic-border-danger)', borderRadius: '8px', color: 'var(--semantic-text-danger)' }}>
          Error loading data: {error.message}
        </div>
      )}

      {/* Reconciliation Tab */}
      {activeTab === 'reconciliation' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <MetricCard
              label="Stripe Payments"
              value={data.reconciliation.stripeTotals.count.toLocaleString()}
              subtitle={`Amount: $${(data.reconciliation.stripeTotals.amount / 100).toFixed(2)}`}
              color="var(--semantic-primary)"
            />
            <MetricCard
              label="Data-Service Payments"
              value={(data.summary?.totalCount || data.payments.length).toLocaleString()}
              subtitle={`Amount: ${data.summary ? `$${((data.summary.totalAmount || 0) / 100).toFixed(2)}` : '—'}`}
              color="var(--semantic-success)"
            />
            <MetricCard
              label="Webhook Events"
              value={(data.reconciliation.webhookSummary?.totalEvents || 0).toLocaleString()}
              subtitle={`Processed: ${data.reconciliation.webhookSummary?.processed || 0}`}
              color="var(--semantic-info)"
            />
            <MetricCard
              label="Webhook Success"
              value={`${(data.reconciliation.webhookSummary?.successRate || 0).toFixed(1)}%`}
              subtitle={`Unprocessed: ${data.reconciliation.webhookSummary?.unprocessed || 0}`}
              color="var(--semantic-warning)"
            />
          </div>

          <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)', marginBottom: '1.5rem' }}>
            <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Mismatch Checks</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Stripe vs Data-Service Count</div>
                <div style={{ fontWeight: 600 }}>
                  {(data.reconciliation.stripeTotals.count - (data.summary?.totalCount || data.payments.length))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Webhook Events vs Stripe Payments</div>
                <div style={{ fontWeight: 600 }}>
                  {(data.reconciliation.webhookSummary?.totalEvents || 0) - data.reconciliation.stripeTotals.count}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Webhook Events vs Data-Service Payments</div>
                <div style={{ fontWeight: 600 }}>
                  {(data.reconciliation.webhookSummary?.totalEvents || 0) - (data.summary?.totalCount || data.payments.length)}
                </div>
              </div>
            </div>
          </div>

          <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Webhook Event Types</h3>
            {data.reconciliation.webhookEventTypes.length === 0 ? (
              <EmptyState message="No webhook events in range" />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Event Type</th>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reconciliation.webhookEventTypes.map((row, idx) => (
                      <tr key={`${row.eventType}-${idx}`}>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>{row.eventType}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)', textAlign: 'right' }}>{Number(row.count || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <>
          {/* Insights */}
          {!loading && insights.length > 0 && (
            <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {insights.map((insight, idx) => (
                <InsightCard key={idx} {...insight} />
              ))}
            </div>
          )}

          {/* KPI Cards */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              {[1, 2, 3, 4, 5].map(i => <MetricCard key={i} loading />)}
            </div>
          ) : (() => {
            const s = data.summary || {};
            const totalCount = s.totalCount || data.payments.length || 0;
            const totalAmount = s.totalAmount || 0;
            const succeededCount = s.succeededCount ?? data.payments.filter(p => p.status === 'succeeded' || p.status === 'completed').length;
            const successRate = totalCount > 0 ? ((succeededCount / totalCount) * 100).toFixed(1) : '0.0';
            const avgPayment = totalCount > 0 ? (totalAmount / totalCount / 100).toFixed(2) : '0.00';
            const latestMrr = data.mrr[data.mrr.length - 1];
            const latestChurn = data.churn[data.churn.length - 1];

            return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <MetricCard
                label="Total Payments"
                value={totalCount.toLocaleString()}
                subtitle={`$${(totalAmount / 100).toFixed(2)} total`}
                color="var(--semantic-primary)"
              />
              <MetricCard
                label="MRR"
                value={`$${(latestMrr?.mrr || 0).toFixed(0)}`}
                subtitle={`ARR: $${(latestMrr?.arr || 0).toFixed(0)}`}
                color="var(--semantic-success)"
                sparklineData={data.mrr.map(m => ({ value: m.mrr }))}
              />
              <MetricCard
                label="Churn Rate"
                value={`${latestChurn?.churnRate || 0}%`}
                subtitle="Monthly churn"
                color={parseFloat(latestChurn?.churnRate || 0) > 5 ? 'var(--semantic-danger)' : 'var(--semantic-warning)'}
              />
              <MetricCard
                label="Success Rate"
                value={`${successRate}%`}
                subtitle={`${succeededCount} of ${totalCount} succeeded`}
                color="var(--semantic-info)"
              />
              <MetricCard
                label="Avg Payment"
                value={`$${avgPayment}`}
                subtitle="Average transaction"
                color="var(--semantic-accent)"
              />
            </div>
            );
          })()}

          {/* MRR/ARR Trend & Churn */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {/* MRR/ARR Trend */}
            <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '.95rem' }}>MRR / ARR Tracking</h3>
                <ExportButton data={data.mrr} filename="mrr-arr" />
              </div>
              {loading ? (
                <LoadingChart height={280} />
              ) : data.mrr.length === 0 ? (
                <EmptyState message="No MRR data" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={data.mrr}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" style={{ fontSize: '.7rem' }} />
                    <YAxis yAxisId="left" style={{ fontSize: '.7rem' }} />
                    <YAxis yAxisId="right" orientation="right" style={{ fontSize: '.7rem' }} />
                    <Tooltip contentStyle={{ fontSize: '.75rem' }} />
                    <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                    <Bar yAxisId="left" dataKey="mrr" fill="#28a745" name="MRR ($)" />
                    <Line yAxisId="right" type="monotone" dataKey="arr" stroke="#007bff" strokeWidth={2} name="ARR ($)" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Churn Analysis */}
            <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Churn Rate</h3>
              {loading ? (
                <LoadingChart height={280} />
              ) : data.churn.length === 0 ? (
                <EmptyState message="No churn data" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.churn}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" style={{ fontSize: '.7rem' }} />
                    <YAxis style={{ fontSize: '.7rem' }} />
                    <Tooltip contentStyle={{ fontSize: '.75rem' }} />
                    <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                    <Line type="monotone" dataKey="churnRate" stroke="#dc3545" strokeWidth={2} name="Churn Rate (%)" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Payment Funnel & Refund Trends */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {/* Payment Funnel */}
            <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Payment Funnel</h3>
              {loading ? (
                <LoadingChart height={300} />
              ) : data.funnel.length === 0 ? (
                <EmptyState message="No funnel data" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <FunnelChart>
                    <Tooltip contentStyle={{ fontSize: '.75rem' }} />
                    <Funnel dataKey="value" data={data.funnel} isAnimationActive>
                      <LabelList position="right" fill="#000" stroke="none" dataKey="name" />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Refund Trends */}
            <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Refund Trends</h3>
              {loading ? (
                <LoadingChart height={300} />
              ) : data.refundTrends.length === 0 ? (
                <EmptyState message="No refunds in this period" icon="✅" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={data.refundTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(v) => new Date(v).toLocaleDateString()}
                      style={{ fontSize: '.7rem' }}
                    />
                    <YAxis yAxisId="left" style={{ fontSize: '.7rem' }} />
                    <YAxis yAxisId="right" orientation="right" style={{ fontSize: '.7rem' }} />
                    <Tooltip 
                      labelFormatter={(v) => new Date(v).toLocaleDateString()}
                      contentStyle={{ fontSize: '.75rem' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                    <Bar yAxisId="left" dataKey="count" fill="#dc3545" name="Refund Count" />
                    <Line yAxisId="right" type="monotone" dataKey="amount" stroke="#ffc107" strokeWidth={2} name="Amount ($)" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Plans Table */}
          <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Available Plans</h3>
            {data.plans.length === 0 ? (
              <EmptyState message="No plans configured" />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Plan</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Price</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>Interval</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Price ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.plans.map((plan, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '0.5rem' }}>{plan.label || plan.alias}</td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                          {plan.amount ? `$${(plan.amount / 100).toFixed(2)}` : 'N/A'}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>{plan.interval || 'N/A'}</td>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '.65rem' }}>
                          {plan.priceId || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Admin Tools Tab */}
      {activeTab === 'admin-tools' && (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Subscription Lookup */}
          <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>🔍 Subscription Lookup</h3>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="text"
                placeholder="Enter User ID"
                value={lookupUserId}
                onChange={(e) => setLookupUserId(e.target.value)}
                style={{ flex: 1, fontSize: '.8rem' }}
              />
              <button onClick={lookupSubscription} disabled={lookupLoading || !lookupUserId}>
                {lookupLoading ? 'Loading...' : 'Lookup'}
              </button>
            </div>

            {lookupError && (
              <div style={{ padding: '0.75rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', fontSize: '.75rem', marginBottom: '1rem' }}>
                {lookupError}
              </div>
            )}

            {subscription && (
              <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', fontSize: '.75rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem' }}>
                  <strong>Status:</strong>
                  <span style={{ 
                    color: subscription.status === 'active' ? '#28a745' : '#dc3545',
                    fontWeight: 600
                  }}>
                    {subscription.status?.toUpperCase() || 'N/A'}
                  </span>
                  
                  <strong>Plan:</strong>
                  <span>{subscription.planAlias || subscription.plan_alias || 'N/A'}</span>
                  
                  <strong>Period End:</strong>
                  <span>
                    {subscription.currentPeriodEnd || subscription.current_period_end 
                      ? new Date(subscription.currentPeriodEnd || subscription.current_period_end).toLocaleString()
                      : 'N/A'}
                  </span>
                  
                  {subscription.stripeSubscriptionId && (
                    <>
                      <strong>Stripe Sub ID:</strong>
                      <span style={{ fontFamily: 'monospace', fontSize: '.65rem' }}>
                        {subscription.stripeSubscriptionId}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Recent Payments Admin Panel */}
          <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '.95rem' }}>Recent Payments</h3>
              <ExportButton data={data.payments} filename="recent-payments" />
            </div>
            
            {loading ? (
              <LoadingChart height={300} />
            ) : data.payments.length === 0 ? (
              <EmptyState message="No payments found" />
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.7rem' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--card-bg)' }}>
                    <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>ID</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: '0.5rem', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payments.slice(0, 50).map(payment => (
                      <tr key={payment.id || payment.paymentId} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '.65rem' }}>
                          {(payment.id || payment.paymentId || '').toString().slice(0, 8)}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          {new Date(payment.createdAt || payment.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                          ${((payment.amount || 0) / 100).toFixed(2)}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            borderRadius: '12px',
                            background: payment.status === 'succeeded' || payment.status === 'completed' ? '#d4edda' : 
                                       payment.status === 'failed' ? '#f8d7da' : '#fff3cd',
                            color: payment.status === 'succeeded' || payment.status === 'completed' ? '#155724' :
                                  payment.status === 'failed' ? '#721c24' : '#856404',
                            fontSize: '.65rem',
                            fontWeight: 600
                          }}>
                            {payment.status || 'unknown'}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem' }}>{payment.provider || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
